import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

function formatError(detail) {
  if (!detail) return 'Authentication failed. Check your token code and try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

function SusTekLogo({ size = 40 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="12" fill="#0A4D3C"/>
      <path d="M14 34V14h4v8h12v-8h4v20h-4v-8H18v8h-4z" fill="white" opacity="0.15"/>
      <circle cx="24" cy="24" r="9" stroke="white" strokeWidth="2.5" fill="none"/>
      <path d="M24 17v7l5 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="24" cy="24" r="1.5" fill="white"/>
      <path d="M10 24h4M34 24h4M24 10v4M24 34v4" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.4"/>
    </svg>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, token);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F6F7F6]" data-testid="login-page">
      {/* Subtle grid pattern */}
      <div className="fixed inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle, #0A4D3C 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} />

      <div className="relative w-full max-w-[400px] mx-4">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-[0_4px_32px_rgba(10,77,60,0.08)] border border-[#E5E9E6] overflow-hidden">
          {/* Header bar */}
          <div className="bg-[#0A4D3C] px-6 py-5">
            <div className="flex items-center gap-3">
              <SusTekLogo size={36} />
              <div>
                <h1 className="font-heading font-bold text-[15px] text-white tracking-tight leading-none">SusTek</h1>
                <p className="text-[9px] tracking-[0.14em] uppercase text-white/45 font-medium mt-1">Sustainability Control Tower</p>
              </div>
            </div>
          </div>

          {/* Authenticator badge */}
          <div className="px-6 pt-5 pb-0">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F0F4F1] rounded-lg border border-[#E5E9E6]">
              <div className="w-5 h-5 rounded-full bg-[#0A4D3C]/10 flex items-center justify-center flex-shrink-0">
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                  <path d="M8 1L2 4v4c0 3.5 2.6 6.4 6 7 3.4-.6 6-3.5 6-7V4L8 1z" stroke="#0A4D3C" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
                  <path d="M6 8l1.5 1.5L10.5 6" stroke="#0A4D3C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="text-[11px] text-[#49564C] font-medium">Secured authentication required</span>
            </div>
          </div>

          {/* Form */}
          <div className="px-6 pt-5 pb-6">
            {error && (
              <div data-testid="login-error" className="mb-4 p-3 bg-[#FDEDED] text-[#C6493A] text-[12px] rounded-lg border border-[#C6493A]/15 flex items-start gap-2">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="flex-shrink-0 mt-0.5">
                  <circle cx="8" cy="8" r="7" stroke="#C6493A" strokeWidth="1.5"/>
                  <path d="M8 5v3.5M8 10.5v.5" stroke="#C6493A" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[10px] font-semibold text-[#49564C] uppercase tracking-[0.08em] mb-1.5">Email Address</label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-[#E5E9E6] rounded-lg text-[13px] text-[#1A1F1B] focus:outline-none focus:ring-2 focus:ring-[#0A4D3C]/20 focus:border-[#0A4D3C] transition-all bg-white placeholder:text-[#93A196]"
                  placeholder="danny@sustek.ai"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-semibold text-[#49564C] uppercase tracking-[0.08em] mb-1.5">Token Code</label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={token}
                  onChange={e => setToken(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-[#E5E9E6] rounded-lg text-[13px] text-[#1A1F1B] focus:outline-none focus:ring-2 focus:ring-[#0A4D3C]/20 focus:border-[#0A4D3C] transition-all bg-white placeholder:text-[#93A196] tracking-[0.2em]"
                  placeholder="Enter token code"
                  required
                  autoComplete="one-time-code"
                />
                <p className="mt-1.5 text-[10px] text-[#93A196] leading-relaxed">
                  Open your authenticator app and enter the current token code.
                </p>
              </div>

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-[#0A4D3C] text-white rounded-lg font-semibold text-[13px] hover:bg-[#0E6B53] active:bg-[#083D30] transition-all duration-150 disabled:opacity-50 mt-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2.5" opacity="0.25"/>
                      <path d="M12 2a10 10 0 019.95 9" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                    </svg>
                    Verifying...
                  </span>
                ) : 'Authenticate'}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-[#93A196] mt-5">
          SusTek &middot; Sustainability Intelligence Platform
        </p>
      </div>
    </div>
  );
}
